import React, { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createNotice } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { 
  ArrowLeft,
  Bell, 
  AlertTriangle, 
  Info, 
  Calendar,
  FileText,
  Tag,
  Image,
  Upload,
  Save,
  Eye,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { NoticePriorityField } from './NoticePriorityField';
import type { NoticePriority } from './noticeConstants';

interface NoticeFormData {
  title: string;
  description: string;
  category: string;
  priority: NoticePriority;
  tags: string;
  images: string[];
  visibility: 'PUBLIC' | 'VERIFIED' | 'SUBSCRIBER';
  isPinned: boolean;
  expiryDate: string;
  confirmVeryHighAlert: boolean;
}

const NOTICE_CATEGORIES = [
  { value: 'announcement', label: 'Announcement', icon: Bell },
  { value: 'warning', label: 'Warning', icon: AlertTriangle },
  { value: 'info', label: 'Information', icon: Info },
  { value: 'maintenance', label: 'Maintenance', icon: null },
  { value: 'event', label: 'Event', icon: Calendar },
  { value: 'general', label: 'General', icon: null }
];


// Tooltip component
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function AddNoticePage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const routerLocation = useLocation();
  const locSlug = resolveLocationSlug(routerLocation.pathname, params.slug, selectedLocation);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<NoticeFormData>({
    title: '',
    description: '',
    category: 'general',
    priority: 'medium',
    tags: '',
    images: [],
    visibility: 'SUBSCRIBER',
    isPinned: false,
    expiryDate: '',
    confirmVeryHighAlert: false,
  });

  const handleChange = (field: keyof NoticeFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!selectedLocation?.id) {
      newErrors.location = 'No location selected';
    }
    if (formData.priority === 'very_high' && !formData.confirmVeryHighAlert) {
      newErrors.confirmVeryHighAlert =
        'You must confirm Very High alert impact before submitting.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const tagsArray = formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [];
      
      await createNotice({
        locationId: selectedLocation!.id,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        tags: tagsArray,
        images: formData.images,
        visibility: formData.visibility,
        isPinned: formData.isPinned,
        submissionType: 'STANDARD' as any,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate) : null,
        confirmVeryHighAlert: formData.confirmVeryHighAlert,
        metadata: {
          priority: formData.priority
        }
      });

      // Navigate back to notices
      handleGoBack();
    } catch (error) {
      console.error('Error creating notice:', error);
      setErrors({ submit: 'Failed to create notice. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(locationSectionPath(locSlug, 'notices'));
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">You must be logged in to post notices.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex h-full">
        {/* Main Content Area - Takes full width excluding left sidebar */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 p-6 pb-32 lg:pb-6">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Bell className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Post New Notice</h1>
                    <p className="text-gray-600">Location: {selectedLocation?.name || 'No location selected'}</p>
                  </div>
                </div>
              </div>

              <form id="notice-form" onSubmit={handleSubmit}>
                {/* Full-width sections */}
                <div className="space-y-6 mb-6">
                  {/* What are you posting? */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">What are you posting?</h3>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="e.g., Scheduled Maintenance Notice, Community Event"
                      required
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="Provide detailed content of the notice, instructions, contact information, important information..."
                      required
                    />
                    {errors.description && (
                      <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.description}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">0/1000 characters</p>
                  </div>
                </div>

                {/* Two-column layout for form fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Category and Priority */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                        <select
                          value={formData.category}
                          onChange={(e) => handleChange('category', e.target.value)}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        >
                          <option value="">Select a category</option>
                          {NOTICE_CATEGORIES.map(category => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
                        <NoticePriorityField
                          value={formData.priority}
                          onChange={(priority) => handleChange('priority', priority)}
                          locationId={selectedLocation?.id}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        />
                      </div>
                    </div>
                    {formData.priority === 'very_high' && (
                      <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.confirmVeryHighAlert}
                            onChange={(e) => handleChange('confirmVeryHighAlert', e.target.checked)}
                            className="mt-1 w-4 h-4 text-red-600 border-red-300 focus:ring-red-500"
                          />
                          <div>
                            <div className="text-sm font-medium text-red-900">
                              Confirm Very High Alert
                            </div>
                            <p className="text-xs text-red-700">
                              This alerts all subscribers immediately and cannot be opted out.
                            </p>
                          </div>
                        </label>
                        {errors.confirmVeryHighAlert && (
                          <p className="mt-2 text-xs text-red-700">{errors.confirmVeryHighAlert}</p>
                        )}
                      </div>
                    )}

                    {/* Images */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Images</h3>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        <div className="text-center">
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 mb-2">Upload images to support your notice</p>
                          <p className="text-sm text-gray-400">PNG, JPG up to 10MB each</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center gap-2">
                          <span>Tags</span>
                          <Tooltip content="Separate tags with commas">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <input
                        type="text"
                        value={formData.tags}
                        onChange={(e) => handleChange('tags', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., maintenance, urgent, facility (separate with commas)"
                      />
                    </div>
                  </div>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
            </div>

            {/* Right Sidebar - Anchored to the right */}
            <div className="hidden lg:flex w-80 border-l border-border flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="notice-form"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.title.trim()}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Posting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Approval Notice */}
              <div className="p-6 border-b border-border">
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>All postings go through an approval process before being published.</span>
                </div>
              </div>

              {/* Additional Options */}
              <div className="p-6 border-b border-border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Options</h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isPinned}
                      onChange={(e) => handleChange('isPinned', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Pin notice</div>
                      <Tooltip content="Display at the top of the notice board">
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>

                </div>
              </div>

              {/* Expiry Date */}
              <div className="p-6 flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Expiry Date (Optional)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center gap-2">
                        <span>Expiry Date</span>
                        <Tooltip content="When notice becomes inactive (leave empty for no automatic deactivation)">
                          <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                        </Tooltip>
                      </div>
                    </label>
                    <input
                      type="date"
                      value={formData.expiryDate}
                      onChange={(e) => handleChange('expiryDate', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-14 left-0 right-0 z-10 lg:hidden bg-card border-t border-border px-4 py-3 shadow-lg">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleGoBack}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="notice-form"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : 'Post'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}


